import { Injectable } from '@angular/core';
import { ClientDateFormat } from '../models/defaults';
import { SessionService } from './session.service';

export type LocaleDateFormat =
	| 'date'
	| 'dateTime'
	| 'dateTimeFull'
	| 'dateTimeShort'
	| 'time'
	| 'timeShort';

export type AppDateFormat = 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD';
type AppDateYearFormat = 'numeric' | '2-digit';
type LocaleDateTimeFormat = Extract<LocaleDateFormat, 'dateTime' | 'dateTimeFull' | 'dateTimeShort'>;

export interface DateLike {
	toDate: () => Date;
}

export type DateValue = string | number | Date | DateLike | null | undefined;

const LOCALE_DATE_FORMAT_PRESETS: Readonly<Record<LocaleDateFormat, Intl.DateTimeFormatOptions>> = {
	date: { year: 'numeric', month: '2-digit', day: '2-digit' },
	dateTime: { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false },
	dateTimeFull: { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false },
	dateTimeShort: { year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false },
	time: { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false },
	timeShort: { hour: '2-digit', minute: '2-digit', hour12: false },
};

const CLIENT_DATE_FORMAT_TO_APP_FORMAT: Record<ClientDateFormat, AppDateFormat> = {
	DMY: 'DD-MM-YYYY',
	MDY: 'MM-DD-YYYY',
	YMD: 'YYYY-MM-DD',
};

const SLASH_YMD_PREFIX: RegExp = /^(\d{4})\/(\d{2})\/(\d{2})(?=[Tt ]|$)/;
const YEAR_FIRST_DATE_ONLY: RegExp = /^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/;
const YEAR_LAST_DATE_ONLY: RegExp = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/;

function normalizeSlashYmdToDashes(value: string): string {
	return value.replace(SLASH_YMD_PREFIX, '$1-$2-$3');
}

function clampIsoFractionalSecondsToMillis(value: string): string {
	return value.replace(/(\.\d{3})\d+Z$/i, '$1Z');
}

const ENDS_WITH_EXPLICIT_TIMEZONE: RegExp = /(?:[zZ]|[+-](?:\d{2}:\d{2}|\d{4}))$/;

function collapseDateTimeWhitespaceToT(value: string): string {
	return value.replace(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)/, '$1T$2');
}

function appendUtcZIfDatetimeHasNoTimezone(value: string): string {
	if (ENDS_WITH_EXPLICIT_TIMEZONE.test(value)) {
		return value;
	}
	if (!/^\d{4}-\d{2}-\d{2}T/.test(value)) {
		return value;
	}
	const naiveUtc: RegExp = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?)(\.\d+)?$/;
	if (!naiveUtc.test(value)) {
		return value;
	}
	return `${value}Z`;
}

function prepareIso8601ForNativeParse(value: string): string {
	return clampIsoFractionalSecondsToMillis(appendUtcZIfDatetimeHasNoTimezone(collapseDateTimeWhitespaceToT(value)));
}

function expandTwoDigitYear(year: number): number {
	return year < 100 ? 2000 + year : year;
}

function localCalendarDateOrNull(year: number, monthIndex: number, day: number): Date | null {
	const date: Date = new Date(year, monthIndex, day);
	if (
		isNaN(date.getTime()) ||
		date.getFullYear() !== year ||
		date.getMonth() !== monthIndex ||
		date.getDate() !== day
	) {
		return null;
	}
	return date;
}

function parseYearFirstDateOnly(value: string): Date | null {
	const match: RegExpMatchArray | null = value.match(YEAR_FIRST_DATE_ONLY);
	if (!match) return null;

	const year: number = parseInt(match[1], 10);
	const monthIndex: number = parseInt(match[2], 10) - 1;
	const day: number = parseInt(match[3], 10);

	return localCalendarDateOrNull(year, monthIndex, day);
}

function resolveYearLastDayAndMonth(
	a: number,
	b: number,
	clientFmt: ClientDateFormat,
): { day: number; monthIndex: number } | null {
	if (a < 1 || b < 1) return null;
	if (a > 31 || b > 31) return null;
	if (a > 12 && b > 12) return null;

	if (a > 12) {
		return { day: a, monthIndex: b - 1 };
	}
	if (b > 12) {
		return { monthIndex: a - 1, day: b };
	}

	if (clientFmt === 'MDY') {
		return { monthIndex: a - 1, day: b };
	}

	return { day: a, monthIndex: b - 1 };
}

function parseYearLastDateOnly(value: string, clientFmt: ClientDateFormat): Date | null {
	const match: RegExpMatchArray | null = value.match(YEAR_LAST_DATE_ONLY);
	if (!match) return null;

	const a: number = parseInt(match[1], 10);
	const b: number = parseInt(match[2], 10);
	const year: number = expandTwoDigitYear(parseInt(match[3], 10));

	const parts: { day: number; monthIndex: number } | null = resolveYearLastDayAndMonth(a, b, clientFmt);
	if (!parts) return null;

	return localCalendarDateOrNull(year, parts.monthIndex, parts.day);
}

function appFormatToClientFormat(format: AppDateFormat = 'DD-MM-YYYY'): ClientDateFormat {
	switch (format) {
		case 'MM-DD-YYYY': return 'MDY';
		case 'YYYY-MM-DD': return 'YMD';
		case 'DD-MM-YYYY':
		default: return 'DMY';
	}
}

export function parseDateValueByAppFormat(value: DateValue, format: AppDateFormat = 'DD-MM-YYYY'): Date | null {
	if (value == null) return null;

	if (value instanceof Date) {
		return isNaN(value.getTime()) ? null : value;
	}

	if (typeof value === 'number') {
		const date: Date = new Date(value);
		return isNaN(date.getTime()) ? null : date;
	}

	if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
		const date: Date = value.toDate();
		return isNaN(date.getTime()) ? null : date;
	}

	if (typeof value !== 'string') return null;

	const trimmed: string = value.trim();
	if (!trimmed) return null;

	const normalized: string = normalizeSlashYmdToDashes(trimmed);
	const clientFmt: ClientDateFormat = appFormatToClientFormat(format);

	const yearFirst: Date | null = parseYearFirstDateOnly(normalized);
	if (yearFirst) return yearFirst;

	const yearLast: Date | null = parseYearLastDateOnly(normalized, clientFmt);
	if (yearLast) return yearLast;

	const forNative: string = prepareIso8601ForNativeParse(normalized);
	const date: Date = new Date(forNative);
	return isNaN(date.getTime()) ? null : date;
}

@Injectable({ providedIn: 'root' })
export class DateFormatService {

	constructor(private sessionService: SessionService) { }

	public formatDate(
		value: DateValue,
		mode: LocaleDateFormat = 'date',
		customOptions?: Intl.DateTimeFormatOptions,
	): string {
		if (!value) return '';

		const date: Date | null = this.parseToDate(value);
		if (!date) return '';

		if (!customOptions) {
			if (mode === 'date') {
				return this.formatByAppPreset(date, this.resolveAppFormat());
			}

			if (this.isDateTimeMode(mode)) {
				return this.formatDateTimeByAppPreset(date, mode);
			}
		}

		return this.formatByIntl(date, mode, customOptions);
	}

	public formatDateForFilename(value: DateValue = new Date(), mode: LocaleDateFormat = 'date'): string {
		return this.formatDate(value, mode)
			.replace(/,\s*/g, ' ')
			.replace(/:/g, '-')
			.replace(/[\\/]/g, '-');
	}

	public buildExportFileName(baseName: string, mode: LocaleDateFormat = 'date'): string {
		const normalizedBaseName: string = baseName.trim().replace(/[:*?\[\]\\\/]/g, '_');
		const dateLabel: string = this.formatDateForFilename(new Date(), mode);

		return dateLabel ? `${normalizedBaseName} (${dateLabel})` : normalizedBaseName;
	}

	public parseToDate(value: DateValue): Date | null {
		if (value == null) return null;

		if (value instanceof Date) {
			return this.getValidDate(value);
		}

		if (typeof value === 'number') {
			return this.getValidDate(new Date(value));
		}

		if (this.isDateLike(value)) {
			return this.getValidDate(value.toDate());
		}

		if (typeof value === 'string') {
			return this.parseStringDate(value);
		}

		return null;
	}

	private getClientDateFormat(): ClientDateFormat {
		return this.sessionService.defaults?.client_date_format ?? 'DMY';
	}

	private isDateLike(value: unknown): value is DateLike {
		return typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as DateLike).toDate === 'function';
	}

	private parseStringDate(value: string): Date | null {
		return parseDateValueByAppFormat(value, this.resolveAppFormat());
	}

	private resolveAppFormat(): AppDateFormat {
		return CLIENT_DATE_FORMAT_TO_APP_FORMAT[this.getClientDateFormat()];
	}

	private formatByAppPreset(date: Date, format: AppDateFormat, yearFormat: AppDateYearFormat = 'numeric'): string {
		const year: string = yearFormat === '2-digit'
			? date.getFullYear().toString().slice(-2)
			: date.getFullYear().toString();
		const month: string = (date.getMonth() + 1).toString().padStart(2, '0');
		const day: string = date.getDate().toString().padStart(2, '0');

		switch (format) {
			case 'MM-DD-YYYY': return `${month}-${day}-${year}`;
			case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
			case 'DD-MM-YYYY':
			default: return `${day}-${month}-${year}`;
		}
	}

	private formatByIntl(date: Date, mode: LocaleDateFormat, customOptions?: Intl.DateTimeFormatOptions): string {
		const options: Intl.DateTimeFormatOptions = customOptions ?? LOCALE_DATE_FORMAT_PRESETS[mode];
		return new Intl.DateTimeFormat('en-GB', options).format(date).replace(/\//g, '-');
	}

	private formatDateTimeByAppPreset(date: Date, mode: LocaleDateTimeFormat): string {
		const yearFormat: AppDateYearFormat = mode === 'dateTimeShort' ? '2-digit' : 'numeric';
		const timeMode: Extract<LocaleDateFormat, 'time' | 'timeShort'> = mode === 'dateTimeFull' ? 'time' : 'timeShort';
		const dateLabel: string = this.formatByAppPreset(date, this.resolveAppFormat(), yearFormat);
		const timeLabel: string = this.formatByIntl(date, timeMode);

		return `${dateLabel}, ${timeLabel}`;
	}

	private getValidDate(value: unknown): Date | null {
		return value instanceof Date && !isNaN(value.getTime()) ? value : null;
	}

	private isDateTimeMode(mode: LocaleDateFormat): mode is LocaleDateTimeFormat {
		return mode === 'dateTime' || mode === 'dateTimeFull' || mode === 'dateTimeShort';
	}
}
